import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import Layer from 'ol/layer/Layer';
import { Coordinate } from 'openlayers';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  EjendomSted,
  KoordinatSted,
  MatrikelSted,
  ProjektSted,
  Sted,
  StedOplysninger
} from 'src/app/api/models';
import { LokaliteterService } from 'src/app/api/services';
import { AppConfig } from 'src/app/config/app-config';
import { AppConfigService } from 'src/app/config/app-config.service';
import { MapCoordinate } from 'src/app/core/components/basemap/models/map-coordinate';
import { MapSted } from 'src/app/core/components/basemap/models/map-sted';
import { CurrentProjektService } from 'src/app/projekter/services/current-projekt/current-projekt.service';
import { StedHelper } from '../../helpers/sted-helper';
import { NotificationService } from '../../notifications/notification.service';
import { TekstResourcePipe } from '../../pipes/tekstresource.pipe';
import { BaseMapComponent } from '../basemap/basemap.component';

@Component({
  selector: 'bom-sted-map',
  templateUrl: './sted-map.component.html',
  styleUrls: ['./sted-map.component.scss'],
})
export class StedMapComponent implements OnInit, OnDestroy {
  @Input()
  stedOplysninger$: Observable<StedOplysninger>;

  @Input()
  isLoading: boolean;

  @Input()
  resetMap$ = new BehaviorSubject<boolean>(false);

  @Input()
  enableMatrikelSelect: boolean;

  @Input()
  enableMeasurement = false;

  @Input()
  konfliktLayers$: Observable<Layer[]> = of([]);

  @Input()
  padding = [70, 15, 15, 250];

  @Input()
  showGoToAddressDetailsButton: boolean;

  @ViewChild(BaseMapComponent, { static: true })
  basemap: BaseMapComponent;

  @Output()
  triggerScrollToAddressDetails = new EventEmitter<void>();

  mapSteder$ = new BehaviorSubject<MapSted[]>(null);
  koordinatSted$ = new BehaviorSubject<Coordinate>(null);
  mapStedFokus$ = new BehaviorSubject<MapSted>(null);
  mapClickEvent$ = new Subject<MapCoordinate>();
  stedOplysninger: StedOplysninger;
  showAllAdrDetails = false;
  isMultipeBFEWarningOpen$ = new BehaviorSubject<boolean>(false);

  private matrikel$ = new Subject<MatrikelSted>();
  private componentDestroyed$ = new Subject();
  private mapSteder: MapSted[] = [];
  private matrikelSteder: MatrikelSted[] = [];

  private clickSelected = false;
  private appConfig: AppConfig;
  private sted: ProjektSted;

  constructor(
    private appConfigService: AppConfigService,
    private currentProjektService: CurrentProjektService,
    private lokaliteterService: LokaliteterService,
    private notificationService: NotificationService,
    private tekstResourcePipe: TekstResourcePipe
  ) { }

  ngOnInit() {
    this.appConfig = this.appConfigService.getConfig();
    this.stedOplysninger$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(sted => !!sted)
      )
      .subscribe(sted => {
        if (!this.clickSelected) {
          this.mapSteder = [];
          this.matrikelSteder = [];
        }
        sted.matrikler.forEach(matrikel => {
          this.setGeometryForSted(matrikel);
        });
      });

    this.currentProjektService.sted$.subscribe(sted => (this.sted = sted));

    this.matrikel$.pipe(takeUntil(this.componentDestroyed$)).subscribe(matrikel => {
      const matrikelSted: MatrikelSted = {
        landsEjerlavsKode: parseInt((matrikel.landsEjerlavsKode as any) as string, 10),
        landsEjerLavsNavn: matrikel.landsEjerLavsNavn,
        matrikelNummer: matrikel.matrikelNummer,
        type: 'Matrikel',
      };

      const currentSted: MapSted = {
        coordinate: [0, 0],
        geometry: {
          geometryWkt: matrikel.geometry,
          geometryWktDetail: matrikel.geometry,
        },
        matrikel: matrikelSted,
      };
      this.addMapSted(currentSted);

      // Set matrikelSteder onInit, after it is handled by subscription of lokaliteterService
      if (this.matrikelSteder.length === 0) {
        matrikelSted.type = 'MatrikelSted';
        this.selectMatrikelSted(matrikelSted);
      }
      this.updateMap(currentSted);
    });

    this.mapClickEvent$.pipe(takeUntil(this.componentDestroyed$)).subscribe(mapCoordinate => {
      if (!this.enableMatrikelSelect || mapCoordinate.resolution > 3) {
        return;
      }
      this.clickSelected = true;

      // Reset locations unless its multiselect
      if (!mapCoordinate.multiSelect) {
        this.mapSteder = [];
        this.matrikelSteder = [];
        this.currentProjektService.sted$.next(null);
      }

      this.lokaliteterService
        .getMatrikelStedByPoint({
          x: Math.round(mapCoordinate.coordinate[0]),
          y: Math.round(mapCoordinate.coordinate[1]),
        })
        .subscribe(response => {
          if (!response) {
            this.setKoordinatSted(
              Math.round(mapCoordinate.coordinate[0]),
              Math.round(mapCoordinate.coordinate[1])
            );
          } else if (!response.externalCallSuccess) {
            this.notificationService.warn(
              this.tekstResourcePipe.transform('Fejl.Notifikation.EksternSystem.Error'),
              null,
              response.correlationId
            );
          } else if (response.externalCallSuccess && response.ejendom === null) {
            this.notificationService.info(
              this.tekstResourcePipe.transform('Umatrikuleret.Tekst'),
              this.tekstResourcePipe.transform('Umatrikuleret.Titel')
            );
          } else {
            if (
              this.sted &&
              response.ejendom.bfeNumre[0] !== (this.sted.steder[0] as EjendomSted).bfeNumre[0]
            ) {
              this.isMultipeBFEWarningOpen$.next(true);
              this.clickSelected = false;
              return;
            }

            const newMatrikel = response.ejendom.matrikler[0];
            this.selectMatrikelSted(newMatrikel);

            if (mapCoordinate.multiSelect && this.sted) {
              const index = (this.sted.steder[0] as EjendomSted).matrikler.findIndex(
                m => m.matrikelNummer === newMatrikel.matrikelNummer
                  && m.landsEjerlavsKode === newMatrikel.landsEjerlavsKode
                  && m.kommuneNr === newMatrikel.kommuneNr);
              if (index > -1) {

                if ((this.sted.steder[0] as EjendomSted).matrikler.length === 1) {
                  return;
                }

                (this.sted.steder[0] as EjendomSted).matrikler.splice(index, 1);
              } else {
                (this.sted.steder[0] as EjendomSted).matrikler = (this.sted
                  .steder[0] as EjendomSted).matrikler.concat(response.ejendom.matrikler);
              }
              this.currentProjektService.setSted(this.sted);
            } else {
              const projektSted = { steder: [response.ejendom] } as ProjektSted;
              this.currentProjektService.setSted(projektSted);
            }
          }
        });
    });

    this.stedOplysninger$.pipe(takeUntil(this.componentDestroyed$)).subscribe(stedOplysninger => {
      this.stedOplysninger = stedOplysninger;

      if (this.stedOplysninger) {
        this.showAllAdrDetails = true;
      }
    });

    this.resetMap$.pipe(takeUntil(this.componentDestroyed$)).subscribe(reset => {
      if (!!reset) {
        this.mapSteder = [];
        this.matrikelSteder = [];
        this.currentProjektService.sted$.next(null);
        this.updateMap(null);
      }
    });
  }

  private selectMatrikelSted(matrikelSted: MatrikelSted) {
    let existingMatrikel: MatrikelSted;
    if (this.mapSteder) {
      existingMatrikel = this.matrikelSteder.find(
        existing =>
          existing.landsEjerlavsKode === matrikelSted.landsEjerlavsKode &&
          existing.landsEjerLavsNavn === matrikelSted.landsEjerLavsNavn &&
          existing.matrikelNummer === matrikelSted.matrikelNummer
      );
    }
    // Fjern matrikel, medmindre det er den sidste
    if (existingMatrikel && this.mapSteder.length !== 1) {
      this.removeMapSted(
        existingMatrikel.landsEjerlavsKode,
        existingMatrikel.landsEjerLavsNavn,
        existingMatrikel.matrikelNummer
      );
      this.matrikelSteder = this.matrikelSteder.filter(sted => sted !== existingMatrikel);
    } else {
      this.matrikelSteder.push(matrikelSted);
    }
  }

  setGeometryForSted(sted: Sted) {
    if (sted) {
      const matrikler: MatrikelSted[] = StedHelper.getMatrikler(sted);

      for (let i = 0; i < matrikler.length; i++) {
        const matrikel = matrikler[i];
        if (matrikel) {
          const existingMatrikel = this.findMapSted(
            matrikel.landsEjerlavsKode,
            matrikel.landsEjerLavsNavn,
            matrikel.matrikelNummer
          );

          if (!existingMatrikel) {
            this.setMatrikel(matrikel);
          } else {
            this.updateMap(this.mapSteder[0]);
          }
        }
      }
      this.updateMap(this.mapSteder[0]);
    }
  }

  setMatrikel(matrikel: MatrikelSted): void {
    const mapSted: MapSted = {
      coordinate: [0, 0],
      geometry: {
        geometryWkt: matrikel.geometry,
        geometryWktDetail: matrikel.geometry,
      },
      matrikel: matrikel,
    };
    this.addMapSted(mapSted);
    this.matrikel$.next(matrikel);
  }

  private updateMap(mapSted: MapSted) {
    this.mapSteder$.next(this.mapSteder);
    if (!this.clickSelected && this.mapSteder.length > 0) {
      if (this.mapSteder.length > 1) {
        this.mapStedFokus$.next(this.GetCombinedGeometry());
      } else {
        this.mapStedFokus$.next(mapSted);
      }
    }
    this.clickSelected = false;
  }

  private GetCombinedGeometry(): MapSted {
    const rawGeometry = this.mapSteder.map(sted => {
      return sted.geometry.geometryWktDetail.replace(/^POLYGON\s*\(|\)\s*$/g, '');
    });
    const geometry: string = 'POLYGON \(' + rawGeometry.join(',') + ')';
    const mapsted: MapSted = {
      coordinate: [0, 0],
      geometry: {
        geometryWkt: geometry,
        geometryWktDetail: geometry,
      },
      matrikel: undefined,
    };
    return mapsted;
  }

  private addMapSted(mapSted: MapSted) {
    const existingMatrikel = this.findMapSted(
      mapSted.matrikel.landsEjerlavsKode,
      mapSted.matrikel.landsEjerLavsNavn,
      mapSted.matrikel.matrikelNummer
    );

    if (!existingMatrikel) {
      this.mapSteder.push(mapSted);
    }
  }

  private removeMapSted(
    landsEjerlavsKode: number,
    landsEjerLavsNavn: string,
    matrikelNummer: string
  ) {
    let existingMatrikel: MapSted;
    if (this.mapSteder) {
      existingMatrikel = this.findMapSted(landsEjerlavsKode, landsEjerLavsNavn, matrikelNummer);
    }

    if (existingMatrikel) {
      this.mapSteder = this.mapSteder.filter(sted => sted !== existingMatrikel);
    }
  }

  private findMapSted(
    landsEjerlavsKode: number,
    landsEjerLavsNavn: string,
    matrikelNummer: string
  ): MapSted {
    let existingMatrikel: MapSted;
    if (this.mapSteder) {
      existingMatrikel = this.mapSteder.find(
        existing =>
          existing.matrikel.landsEjerlavsKode === landsEjerlavsKode &&
          existing.matrikel.landsEjerLavsNavn === landsEjerLavsNavn &&
          existing.matrikel.matrikelNummer === matrikelNummer
      );
    }
    return existingMatrikel;
  }

  private setKoordinatSted(x: number, y: number) {
    const wkt = `POINT(${x} ${y})`;
    const koordinatSted: KoordinatSted = {
      wktGeometry: wkt,
      type: 'KoordinatSted', // StedType enum?
    };
    const projectSted: ProjektSted = {
      steder: [koordinatSted],
    };
    this.currentProjektService.setSted(projectSted);
    this.koordinatSted$.next([x, y]);
  }

  private createWktData(coordinates: number[][]) {
    let polygon = 'POLYGON((';
    const temp: string[] = [];

    coordinates.forEach(coordinate => {
      temp.push(coordinate.join(' '));
    });

    polygon = polygon.concat(temp.join(',') + '))');
    return polygon;
  }

  setLayerVisible(isChecked: boolean, id: string) {
    this.basemap.setLayerVisible(isChecked, id);
  }

  scrollToAddressDetailsElement(): void {
    this.triggerScrollToAddressDetails.emit();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}

